import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { STATUSES, PROS } from "../lib/statuses";
import VerifyPanel from "./VerifyPanel";

const SECTIONS = [
  { key: "core", icon: "♪", label: "Core" },
  { key: "metadata", icon: "⌗", label: "Metadata" },
  { key: "credits", icon: "⌘", label: "Credits" },
  { key: "registrations", icon: "§", label: "Registrations" },
  { key: "notes", icon: "✎", label: "Notes" },
];

export default function TrackEditModal({ open, track, onClose, onSaved, onDelete, projects = [], onProjectCreated }) {
  const isNew = !track?.id;
  const [form, setForm] = useState({});
  const [open_section, setOpenSection] = useState("core");
  const [showVerify, setShowVerify] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        title: "", primary_artist: "", features: "", status: "idea",
        release_date: "", isrc: "", iswc: "", upc: "", duration: "",
        bpm: "", song_key: "", genre: "", label: "", producers: "",
        writers: "", mix_engineer: "", mastering_engineer: "", pro: "",
        publisher: "", copyright_reg: "", soundexchange_flag: false,
        mlc_flag: false, songtrust_flag: false, distributor: "", notes: "",
        artwork_url: "",
        ...(track || {}),
        ascap_work_id: track?.external_ids?.ascap_work_id || "",
      });
      setOpenSection("core");
      setShowVerify(false);
    }
  }, [open, track]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const project = projects.find((p) => p.id === form.project_id);
  const cover = form.artwork_url || project?.cover_art_url;

  const applyFresh = (fresh) => {
    setForm((f) => ({ ...f, isrc: fresh.isrc, external_ids: fresh.external_ids,
      completeness_score: fresh.completeness_score, completeness_breakdown: fresh.completeness_breakdown }));
    onSaved?.(fresh);
  };

  const jumpTo = (key) => {
    setOpenSection(key);
    setTimeout(() => document.querySelector(`[data-testid="section-${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const onSave = async () => {
    if (!form.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, mlc_via: form.external_ids?.mlc_via || "" };
      if (payload.bpm === "" || payload.bpm === null) delete payload.bpm;
      else payload.bpm = Number(payload.bpm);
      if (!payload.release_date) delete payload.release_date;

      let r;
      if (isNew) {
        r = await api.post("/tracks", payload);
        toast.success("Track created");
      } else {
        r = await api.patch(`/tracks/${track.id}`, payload);
        toast.success("Track saved");
      }
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        onClose?.();
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const onDeleteClick = async () => {
    if (!window.confirm("Delete this track? This cannot be undone.")) return;
    await onDelete?.(track);
    onClose?.();
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="track-modal">
      <div
        className="modal-card"
        style={{ maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "22px 24px 14px", borderBottom: "1px solid var(--border-soft)" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-end" }}>
          <div className="modal-cover" style={{ background: cover ? `url("${cover}") center/cover` : undefined }} data-testid="track-modal-cover" title={form.artwork_url ? "Track artwork" : project ? "Inherited from project" : "No artwork"} />
          <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow" style={{ color: "var(--accent)" }}>
            — {isNew ? "New Entry" : "Edit Entry"}{project ? ` · ${project.title}` : ""}
          </div>
          <input
            data-testid="track-title-input"
            className="lq-input"
            placeholder="Track title"
            value={form.title || ""}
            onChange={(e) => set("title", e.target.value)}
            style={{
              fontFamily: "var(--serif)",
              fontStyle: "italic",
              fontSize: 28,
              border: 0,
              padding: "8px 0",
              background: "transparent",
            }}
          />
          </div>
          </div>
        </div>

        <div style={{ padding: "0 24px" }}>
          {SECTIONS.map((s) => (
            <Section
              key={s.key}
              s={s}
              open={open_section === s.key}
              onToggle={() => setOpenSection(open_section === s.key ? null : s.key)}
            >
              {s.key === "core" && <CoreSection form={form} set={set} projects={projects} onProjectCreated={onProjectCreated} />}
              {s.key === "metadata" && <MetadataSection form={form} set={set} trackId={track?.id} onReviewed={applyFresh} />}
              {s.key === "credits" && <CreditsSection form={form} set={set} />}
              {s.key === "registrations" && <RegistrationsSection form={form} set={set} setForm={setForm} />}
              {s.key === "notes" && <NotesSection form={form} set={set} />}
            </Section>
          ))}

          {!isNew && (
            <div style={{ borderTop: "1px solid var(--border-soft)", paddingTop: 16, marginTop: 8 }}>
              <button className="btn btn-light btn-block" onClick={() => setShowVerify((v) => !v)} data-testid="toggle-verify-btn">
                {showVerify ? "Hide Verify Panel" : "↗ Open Verify Panel"}
              </button>
              {showVerify && <VerifyPanel track={form} onTrackChange={applyFresh} onJump={jumpTo} />}
            </div>
          )}
        </div>

        <div
          style={{
            position: "sticky", bottom: 0, padding: "14px 24px",
            background: "var(--bg-elev)", borderTop: "1px solid var(--border-soft)",
            display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap",
          }}
        >
          {isNew ? <span /> : (
            <button className="btn btn-danger" onClick={onDeleteClick} data-testid="delete-track-btn">
              Delete
            </button>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} data-testid="cancel-track-btn">Cancel</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving} data-testid="save-track-btn">
              {saving ? "Saving…" : (isNew ? "Create Track" : "Save Changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ s, open, onToggle, children }) {
  return (
    <div className="collapsible">
      <div className="collapsible-head" onClick={onToggle} data-testid={`section-${s.key}`}>
        <div className="collapsible-name">
          <span className="collapsible-icon">{s.icon}</span>{s.label}
        </div>
        <span style={{ color: "var(--text-fade)" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="collapsible-body">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ marginBottom: 12 }}><label className="label">{label}</label>{children}</div>;
}

function Grid({ children }) {
  return <div className="settings-grid-2">{children}</div>;
}

function CoreSection({ form, set, projects, onProjectCreated }) {
  return (
    <>
      <ProjectPicker form={form} set={set} projects={projects} onProjectCreated={onProjectCreated} />
      <Grid>
        <Field label="Primary Artist">
          <input data-testid="track-artist" className="lq-input" value={form.primary_artist || ""} onChange={(e) => set("primary_artist", e.target.value)} />
        </Field>
        <Field label="Features">
          <input data-testid="track-features" className="lq-input" placeholder="ft. ..." value={form.features || ""} onChange={(e) => set("features", e.target.value)} />
        </Field>
      </Grid>
      <Grid>
        <Field label="Status">
          <select data-testid="track-status" className="lq-select" value={form.status || "idea"} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Release Date">
          <input data-testid="track-release-date" className="lq-input" type="date" value={form.release_date || ""} onChange={(e) => set("release_date", e.target.value)} />
        </Field>
      </Grid>
      <Field label="Label">
        <input data-testid="track-label" className="lq-input" value={form.label || ""} onChange={(e) => set("label", e.target.value)} />
      </Field>
    </>
  );
}

function ProjectPicker({ form, set, projects, onProjectCreated }) {
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const create = async () => {
    if (!title.trim()) return;
    try {
      const r = await api.post("/projects", { title: title.trim(), primary_artist: form.primary_artist || "", type: "album" });
      onProjectCreated?.(r.data);
      set("project_id", r.data.id);
      setCreating(false);
      setTitle("");
      toast.success("Project created — save to attach");
    } catch {
      toast.error("Could not create project");
    }
  };
  return (
    <Field label="Project">
      {creating ? (
        <div style={{ display: "flex", gap: 8 }}>
          <input autoFocus data-testid="new-project-title" className="lq-input" placeholder="Project title" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
          <button type="button" className="btn btn-primary" onClick={create} data-testid="new-project-create">Create</button>
          <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)} data-testid="new-project-cancel">×</button>
        </div>
      ) : (
        <select
          data-testid="track-project"
          className="lq-select"
          value={form.project_id || ""}
          onChange={(e) => (e.target.value === "__new" ? setCreating(true) : set("project_id", e.target.value))}
        >
          <option value="">None</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.title} · {String(p.type || "").toUpperCase()}</option>)}
          <option value="__new">+ Create new project…</option>
        </select>
      )}
    </Field>
  );
}

function IsrcReview({ form, trackId, onReviewed }) {
  const [busy, setBusy] = useState(false);
  if (!trackId || !form.external_ids?.isrc_needs_review) return null;
  const cands = form.external_ids.isrc_candidates || [];
  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/isrc/review/${trackId}`, { action });
      onReviewed(r.data);
      toast.success(action === "confirm" ? "ISRC confirmed" : "ISRC cleared");
    } catch {
      toast.error("Could not update ISRC");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="isrc-review" data-testid="isrc-review">
      <div>
        <span className="review-tag">ISRC · NEEDS REVIEW</span>
        <span className="data-line" style={{ marginLeft: 8 }}>
          Auto-filled from Deezer{cands.length > 1 ? ` · ${cands.length} matches: ${cands.join(", ")}` : ""}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" className="connection-btn primary" disabled={busy} onClick={() => act("confirm")} data-testid="isrc-confirm-btn">Confirm</button>
        <button type="button" className="connection-btn danger" disabled={busy} onClick={() => act("clear")} data-testid="isrc-clear-btn">Clear</button>
      </div>
    </div>
  );
}

function MetadataSection({ form, set, trackId, onReviewed }) {
  return (
    <>
      <IsrcReview form={form} trackId={trackId} onReviewed={onReviewed} />
      <Grid>
        <Field label="ISRC"><input data-testid="track-isrc" className="lq-input" value={form.isrc || ""} onChange={(e) => set("isrc", e.target.value)} /></Field>
        <Field label="ISWC"><input data-testid="track-iswc" className="lq-input" value={form.iswc || ""} onChange={(e) => set("iswc", e.target.value)} /></Field>
        <Field label="UPC"><input className="lq-input" value={form.upc || ""} onChange={(e) => set("upc", e.target.value)} /></Field>
        <Field label="Duration"><input className="lq-input" placeholder="3:42" value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} /></Field>
        <Field label="BPM"><input data-testid="track-bpm" className="lq-input" type="number" value={form.bpm ?? ""} onChange={(e) => set("bpm", e.target.value)} /></Field>
        <Field label="Key"><input data-testid="track-key" className="lq-input" placeholder="F# minor" value={form.song_key || ""} onChange={(e) => set("song_key", e.target.value)} /></Field>
        <Field label="Genre"><input className="lq-input" value={form.genre || ""} onChange={(e) => set("genre", e.target.value)} /></Field>
        <Field label="Distributor"><input className="lq-input" value={form.distributor || ""} onChange={(e) => set("distributor", e.target.value)} /></Field>
      </Grid>
    </>
  );
}

function CreditsSection({ form, set }) {
  return (
    <Grid>
      <Field label="Producers"><input className="lq-input" value={form.producers || ""} onChange={(e) => set("producers", e.target.value)} /></Field>
      <Field label="Writers / Composers"><input className="lq-input" value={form.writers || ""} onChange={(e) => set("writers", e.target.value)} /></Field>
      <Field label="Mix Engineer"><input className="lq-input" value={form.mix_engineer || ""} onChange={(e) => set("mix_engineer", e.target.value)} /></Field>
      <Field label="Mastering Engineer"><input className="lq-input" value={form.mastering_engineer || ""} onChange={(e) => set("mastering_engineer", e.target.value)} /></Field>
    </Grid>
  );
}

function RegistrationsSection({ form, set, setForm }) {
  const mlcViaSongtrust = form.external_ids?.mlc_via === "songtrust";
  const onSongtrustToggle = (v) => {
    setForm((f) => {
      const nextExt = { ...(f.external_ids || {}) };
      if (v) {
        if (!f.mlc_flag) nextExt.mlc_via = "songtrust";
        return { ...f, songtrust_flag: true, mlc_flag: true, external_ids: nextExt };
      }
      const clearMlc = nextExt.mlc_via === "songtrust";
      delete nextExt.mlc_via;
      return { ...f, songtrust_flag: false, mlc_flag: clearMlc ? false : f.mlc_flag, external_ids: nextExt };
    });
  };
  const onMlcToggle = (v) => {
    setForm((f) => {
      const nextExt = { ...(f.external_ids || {}) };
      delete nextExt.mlc_via;
      return { ...f, mlc_flag: v, external_ids: nextExt };
    });
  };
  return (
    <>
      <Grid>
        <Field label="PRO">
          <select data-testid="track-pro" className="lq-select" value={form.pro || ""} onChange={(e) => set("pro", e.target.value)}>
            {PROS.map((p) => <option key={p} value={p}>{p || "—"}</option>)}
          </select>
        </Field>
        <Field label="Publisher"><input className="lq-input" value={form.publisher || ""} onChange={(e) => set("publisher", e.target.value)} /></Field>
        <Field label="Copyright Reg #"><input className="lq-input" value={form.copyright_reg || ""} onChange={(e) => set("copyright_reg", e.target.value)} /></Field>
        <Field label="ASCAP Work ID"><input data-testid="track-ascap-work-id" className="lq-input" placeholder="Upgrades the Songview link" value={form.ascap_work_id || ""} onChange={(e) => set("ascap_work_id", e.target.value)} /></Field>
      </Grid>
      <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
        <Toggle label="SoundExchange registered" checked={form.soundexchange_flag} onChange={(v) => set("soundexchange_flag", v)} testid="toggle-soundexchange" />
        <Toggle
          label={mlcViaSongtrust ? "MLC · via Songtrust" : "MLC registered"}
          checked={form.mlc_flag}
          onChange={onMlcToggle}
          dimmed={mlcViaSongtrust}
          testid="toggle-mlc"
        />
        <Toggle label="Songtrust registered" checked={form.songtrust_flag} onChange={onSongtrustToggle} testid="toggle-songtrust" />
      </div>
    </>
  );
}

function NotesSection({ form, set }) {
  return (
    <Field label="Notes">
      <textarea className="lq-textarea" rows={5} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
    </Field>
  );
}

function Toggle({ label, checked, onChange, dimmed, testid }) {
  return (
    <label
      data-testid={testid}
      style={{
        display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
        fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: dimmed ? "var(--text-fade)" : "var(--text-mute)",
      }}
    >
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: "var(--accent)" }} />
      {label}
    </label>
  );
}
